<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div>
          <p class="text-uppercase text-muted mb-1 super-page-kicker">Configuration</p>
          <h2 class="h3 mb-0">Site Management</h2>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="row align-items-end">
            <div class="col-lg-7 mb-3 mb-lg-0">
              <p class="super-page-kicker mb-2">Sites</p>
              <h3 class="mb-2">Review and maintain configured sites</h3>
              <p class="text-muted mb-0">
                Search current results, inspect lot details, and update site configuration from one page.
              </p>
            </div>
            <div class="col-lg-5">
              <div class="row align-items-end">
                <div class="col-md-8 mb-3 mb-md-0">
                  <label class="form-control-label">Search</label>
                  <b-form-input v-model.trim="searchTerm" placeholder="Site, provider, or service"></b-form-input>
                </div>
                <div class="col-md-4 text-md-right">
                  <router-link :to="{ name: 'addNewSite' }" class="btn btn-primary btn-block">Add Site</router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border-0">
        <div class="card-header bg-white border-0 py-3">
          <div class="d-flex justify-content-between align-items-center">
            <h3 class="mb-0">Site List</h3>
            <span class="badge badge-pill badge-primary">{{ filteredSites.length }} visible</span>
          </div>
        </div>

        <div v-if="isBusy" class="text-center py-6">
          <b-spinner variant="primary" class="mb-3"></b-spinner>
          <p class="text-muted mb-0">Loading sites...</p>
        </div>

        <div v-else-if="filteredSites.length" class="table-responsive">
          <b-table class="mb-0" striped hover :items="filteredSites" :fields="fields">
            <template slot="index" slot-scope="data">
              {{ data.index + (siteList.from || 1) }}
            </template>
            <template slot="site_mapping" slot-scope="data">
              {{ data.value.name }}
            </template>
            <template slot="provider_name" slot-scope="data">
              {{ data.value.name }}
            </template>
            <template slot="service_type" slot-scope="data">
              {{ data.value.service_name }}
            </template>
            <template slot="meter_count" slot-scope="data">
              {{ data.value }}
            </template>
            <template slot="actions" slot-scope="data">
              <div class="d-flex justify-content-center align-items-center">
                <b-button size="sm" variant="outline-primary" class="mr-2" @click="editSite(data.item)">
                  Edit
                </b-button>
                <b-button size="sm" variant="outline-secondary" @click="viewSite(data.item)">
                  View
                </b-button>
              </div>
            </template>
          </b-table>
          <div class="card-footer border-0 bg-white">
            <b-pagination align="right" @input="pageClick" :total-rows="siteList.total || 0"
              v-model="currentPage" :per-page="10">
            </b-pagination>
          </div>
        </div>

        <div v-else class="text-center py-6">
          <h5 class="mb-2">No records found</h5>
          <p class="text-muted mb-0">Try another search term or add a site.</p>
        </div>
      </div>

      <b-modal no-close-on-backdrop no-close-on-esc centered size="lg" hide-footer ref="editSite" id="editSite"
        title="Edit Site" :hide-header-close="true">
        <b-form>
          <div class="row m-0 mb-4">
            <b-form-group label="Site Name" label-for="siteName" class="col-md-4">
              <b-form-input id="siteName" type="text" v-model="form.name" placeholder="Enter site short name">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Site Full Name" label-for="siteFullName" class="col-md-4">
              <b-form-input id="siteFullName" type="text" v-model="form.fullname" placeholder="Enter site full name">
              </b-form-input>
            </b-form-group>
            <b-form-group id="serviceProvider" label="Service Provider" label-for="serviceProvider" class="col-md-4">
              <b-form-select v-model="form.service_provider_id" class="form-control custom-select">
                <option :value="null" disabled>Select service provider</option>
                <option v-for="sp in serviceProviders" :key="sp.id" :value="sp.id">{{ sp.name }}</option>
              </b-form-select>
            </b-form-group>
          </div>

          <div class="row m-0 mb-4" v-if="form.water.status">
            <h5 class="col-md-12">Water</h5>
            <b-form-group label="Description" label-for="waterDescription" class="col-md-12">
              <b-form-textarea id="waterDescription" v-model="form.water.description"
                placeholder="Enter water description"></b-form-textarea>
            </b-form-group>
            <b-form-group label="CWA Rate" label-for="cwaRate" class="col-md-4">
              <b-form-select v-model="form.water.cwa_rate_management_id" class="form-control custom-select">
                <option :value="null" disabled>Select CWA Rate</option>
                <option v-for="rate in rateList.data" :key="rate.id" :value="rate.id">{{ rate.name }}</option>
              </b-form-select>
            </b-form-group>
            <b-form-group label="WMA Rate" label-for="wmaRate" class="col-md-4">
              <b-form-select v-model="form.water.wma_rate_management_id" class="form-control custom-select">
                <option :value="null" disabled>Select WMA Rate</option>
                <option v-for="rate in rateList.data" :key="rate.id" :value="rate.id">{{ rate.name }}</option>
              </b-form-select>
            </b-form-group>
            <b-form-group label="Payment Terms (In Days)" label-for="waterPaymentTerms" class="col-md-4">
              <b-form-input type="text" id="waterPaymentTerms" v-model="form.water.payment_terms"
                placeholder="Enter payment terms">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Service Charge (If Billable)" label-for="waterServiceCharge" class="col-md-4">
              <b-form-input type="text" id="waterServiceCharge" v-model="form.water.service_charge"
                placeholder="Enter service charge">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Meter Rental (If Billable)" label-for="waterMeterRental" class="col-md-4">
              <b-form-input type="text" id="waterMeterRental" v-model="form.water.meter_rental"
                placeholder="Enter meter rental">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Custom Message" label-for="waterMsg" class="col-md-12">
              <b-form-textarea id="waterMsg" v-model="form.water.custom_message" placeholder="Enter custom message">
              </b-form-textarea>
            </b-form-group>
          </div>

          <div class="row m-0 mb-4" v-if="form.electricity.status">
            <h5 class="col-md-12">Electricity</h5>
            <b-form-group label="Description" label-for="electricityDescription" class="col-md-12">
              <b-form-textarea id="electricityDescription" v-model="form.electricity.description"
                placeholder="Enter electricity description">
              </b-form-textarea>
            </b-form-group>
            <b-form-group label="Rate" label-for="electricityRate" class="col-md-4">
              <b-form-select v-model="form.electricity.rate_management_id" class="form-control custom-select">
                <option :value="null" disabled>Select rate</option>
                <option v-for="rate in rateList.data" :key="rate.id" :value="rate.id">{{ rate.name }}</option>
              </b-form-select>
            </b-form-group>
            <b-form-group label="Payment Terms (In Days)" label-for="electricityPaymentTerms" class="col-md-4">
              <b-form-input type="text" id="electricityPaymentTerms" v-model="form.electricity.payment_terms"
                placeholder="Enter payment terms">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Service Charge (If Billable)" label-for="electricityServiceCharge" class="col-md-4">
              <b-form-input type="text" id="electricityServiceCharge" v-model="form.electricity.service_charge"
                placeholder="Enter service charge">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Meter Rental (If Billable)" label-for="electricityMeterRental" class="col-md-4">
              <b-form-input type="text" id="electricityMeterRental" v-model="form.electricity.meter_rental"
                placeholder="Enter meter rental">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Custom Message" label-for="electricityMsg" class="col-md-12">
              <b-form-textarea id="electricityMsg" v-model="form.electricity.custom_message"
                placeholder="Enter custom message">
              </b-form-textarea>
            </b-form-group>
          </div>

          <div class="row m-0 mb-4" v-if="form.gas.status">
            <h5 class="col-md-12">Gas</h5>
            <b-form-group label="Description" label-for="gasDescription" class="col-md-12">
              <b-form-textarea id="gasDescription" v-model="form.gas.description"
                placeholder="Enter gas description">
              </b-form-textarea>
            </b-form-group>
            <b-form-group label="Rate" label-for="gasRate" class="col-md-4">
              <b-form-select v-model="form.gas.rate_management_id" class="form-control custom-select">
                <option :value="null" disabled>Select rate</option>
                <option v-for="rate in rateList.data" :key="rate.id" :value="rate.id">{{ rate.name }}</option>
              </b-form-select>
            </b-form-group>
            <b-form-group label="Payment Terms (In Days)" label-for="gasPaymentTerms" class="col-md-4">
              <b-form-input type="text" id="gasPaymentTerms" v-model="form.gas.payment_terms"
                placeholder="Enter payment terms">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Service Charge (If Billable)" label-for="gasServiceCharge" class="col-md-4">
              <b-form-input type="text" id="gasServiceCharge" v-model="form.gas.service_charge"
                placeholder="Enter service charge">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Meter Rental (If Billable)" label-for="gasMeterRental" class="col-md-4">
              <b-form-input type="text" id="gasMeterRental" v-model="form.gas.meter_rental"
                placeholder="Enter meter rental">
              </b-form-input>
            </b-form-group>
            <b-form-group label="Custom Message" label-for="gasMsg" class="col-md-12">
              <b-form-textarea id="gasMsg" v-model="form.gas.custom_message" placeholder="Enter custom message">
              </b-form-textarea>
            </b-form-group>
          </div>

          <div class="row m-0">
            <div class="col-md-12 text-right">
              <b-link @click="hideModal">Cancel</b-link>
              <b-button @click="changeServiceProvider" type="button" variant="warning" class="ml-2" :disabled="loading">
                Change Service Provider
              </b-button>
              <b-button @click="updateSite(form)" type="button" variant="success" class="ml-3" :disabled="loading">
                Save
              </b-button>
            </div>
          </div>
        </b-form>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import userData from '@/pages/Common/userData';

export default {
  components: {
    userData
  },
  data() {
    return {
      currentPage: 1,
      isBusy: false,
      loading: false,
      searchTerm: '',
      fields: [
        { key: 'index', label: 'S.No', sortable: true, class: 'text-center' },
        { key: 'site_mapping', label: 'Name', sortable: true, class: 'text-center' },
        { key: 'provider_name', label: 'Service Provider', sortable: true, class: 'text-center' },
        { key: 'service_type', label: 'Meter Type', sortable: true, class: 'text-center' },
        { key: 'meter_count', label: 'Meter Count', sortable: true, class: 'text-center' },
        { key: 'actions', label: 'Actions', sortable: false, class: 'text-center' }
      ],
      form: {
        name: '',
        fullname: '',
        service_provider_id: null,
        water: {
          status: false,
          custom_message: '',
          description: '',
          cwa_rate_management_id: '',
          wma_rate_management_id: '',
          service_charge: '',
          meter_rental: '',
          payment_terms: ''
        },
        electricity: {
          status: false,
          rate_management_id: '',
          description: '',
          service_charge: '',
          meter_rental: '',
          custom_message: '',
          payment_terms: '',
          load_based: false
        },
        gas: {
          status: false,
          rate_management_id: '',
          description: '',
          service_charge: '',
          meter_rental: '',
          payment_terms: '',
          custom_message: ''
        }
      },
      serviceProviders: []
    }
  },
  computed: {
    ...mapGetters('siteManagement', ['siteList', 'serviceProviderList', 'rateList']),
    filteredSites() {
      const searchTerm = this.searchTerm.toLowerCase()
      const siteRows = this.siteList && this.siteList.data ? this.siteList.data : []
      if (!searchTerm) {
        return siteRows
      }
      return siteRows.filter((item) => {
        const siteName = item.site_mapping && item.site_mapping.name ? item.site_mapping.name.toLowerCase() : ''
        const providerName = item.provider_name && item.provider_name.name ? item.provider_name.name.toLowerCase() : ''
        const serviceName = item.service_type && item.service_type.service_name ? item.service_type.service_name.toLowerCase() : ''
        return siteName.includes(searchTerm) || providerName.includes(searchTerm) || serviceName.includes(searchTerm)
      })
    }
  },
  methods: {
    ...mapActions('siteManagement', ['SITE_LIST', 'FETCH_SERVICE_PROVIDER', 'FETCH_RATES', 'UPDATE_SITE', 'CHANGE_SERVICE_PROVIDER']),
    getSiteList(page = this.currentPage) {
      this.isBusy = true
      this.SITE_LIST(page)
        .then(() => {
          this.currentPage = page
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    pageClick(page) {
      this.getSiteList(page)
    },
    viewSite(data) {
      this.$router.replace({
        name: 'siteDetailView',
        params: {
          serviceproviderid: data.provider_name.id,
          servicetypeid: data.service_type_id,
          siteid: data.site_id
        }
      })
    },
    editSite(data) {
      this.form = {
        id: data.site_id,
        name: data.site_mapping.name,
        fullname: data.site_mapping.fullname,
        service_provider_id: data.site_mapping.service_provider_id
      }

      this.form.water = data.service_type.id === 1
        ? {
          status: true,
          id: data.water_service.id,
          custom_message: data.water_service.custom_message,
          description: data.water_service.description,
          cwa_rate_management_id: data.water_service.cwa_rate_management_id,
          wma_rate_management_id: data.water_service.wma_rate_management_id,
          service_charge: data.water_service.service_charge,
          meter_rental: data.water_service.meter_rental,
          payment_terms: data.water_service.payment_terms
        }
        : { status: false }

      this.form.electricity = data.service_type.id === 2
        ? {
          status: true,
          id: data.electricity_service.id,
          rate_management_id: data.electricity_service.rate_management_id,
          description: data.electricity_service.description,
          service_charge: data.electricity_service.service_charge,
          meter_rental: data.electricity_service.meter_rental,
          custom_message: data.electricity_service.custom_message,
          payment_terms: data.electricity_service.payment_terms,
          load_based: data.electricity_service.load_based
        }
        : { status: false }

      this.form.gas = data.service_type.id === 3
        ? {
          status: true,
          id: data.gas_service.id,
          rate_management_id: data.gas_service.rate_management_id,
          description: data.gas_service.description,
          service_charge: data.gas_service.service_charge,
          meter_rental: data.gas_service.meter_rental,
          payment_terms: data.gas_service.payment_terms,
          custom_message: data.gas_service.custom_message
        }
        : { status: false }

      this.$refs.editSite.show()
      this.FETCH_SERVICE_PROVIDER().then(() => {
        this.serviceProviders = this.serviceProviderList.data || []
      }).catch(() => {})
      this.FETCH_RATES().catch(() => {})
    },
    updateSite(data) {
      this.loading = true
      this.UPDATE_SITE(data)
        .then(() => {
          this.hideModal()
          this.getSiteList(1)
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    changeServiceProvider() {
      this.loading = true
      const data = {
        site_id: this.form.id,
        service_provider_id: this.form.service_provider_id
      }
      this.CHANGE_SERVICE_PROVIDER(data)
        .then((res) => {
          this.SUCCESS_TOAST_MSG(res.message || 'Syndic updated successfully')
          this.hideModal()
          this.getSiteList(this.currentPage)
        })
        .catch((err) => {
          this.ERROR_TOAST_MSG(err.message || 'Failed to update syndic')
        })
        .finally(() => {
          this.loading = false
        })
    },
    hideModal() {
      this.$refs.editSite.hide()
    }
  },
  created() {
    this.getSiteList(1)
  }
}
</script>

<style scoped>
.super-page-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #5e72e4;
}
</style>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/siteManagement.vue